'use client';

import { useState, useEffect } from 'react';
import { useParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import { Title } from '@/components/ui/Headings';

type CookiePreferences = {
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
};

const COOKIE_PREFERENCES_NAME = 'revoyager_cookie_preferences';
const COOKIE_CONSENT_NAME = 'revoyager_cookie_consent';

export default function CookieSettingsPage() {
  const params = useParams();
  const router = useRouter();
  const locale = (params?.locale as string) || 'en';

  const [preferences, setPreferences] = useState<CookiePreferences>({
    functional: true,
    analytics: false,
    marketing: false,
  });

  const [saved, setSaved] = useState(false);

  useEffect(() => {
    // Load saved preferences
    const savedPrefs = localStorage.getItem(COOKIE_PREFERENCES_NAME);
    if (savedPrefs) {
      try {
        setPreferences(JSON.parse(savedPrefs));
      } catch (e) {
        console.error('Failed to parse cookie preferences:', e);
      }
    }
  }, []);

  const savePreferences = () => {
    localStorage.setItem(COOKIE_CONSENT_NAME, 'true');
    localStorage.setItem(COOKIE_PREFERENCES_NAME, JSON.stringify(preferences));

    // Dispatch event for other components
    window.dispatchEvent(new CustomEvent('cookiePreferencesChanged', { detail: preferences }));

    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const clearAllCookies = () => {
    if (confirm(t.clearConfirm)) {
      localStorage.removeItem(COOKIE_CONSENT_NAME);
      localStorage.removeItem(COOKIE_PREFERENCES_NAME);
      setPreferences({
        functional: true,
        analytics: false,
        marketing: false,
      });
      router.refresh();
    }
  };

  const translations = {
    en: {
      title: 'Cookie Settings',
      description: 'Manage your cookie preferences for this website. Functional cookies are always enabled as they are necessary for the website to work properly.',
      functional: 'Functional Cookies',
      functionalDesc: 'These cookies are essential for the website to function properly. They enable basic features like authentication, security, and session management. These cookies cannot be disabled.',
      functionalDetails: 'Examples: Authentication tokens (Clerk), session management, security tokens, language preferences.',
      analytics: 'Analytics Cookies',
      analyticsDesc: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our website and services.',
      analyticsDetails: 'Examples: Google Analytics, page view tracking, user behavior analysis.',
      marketing: 'Marketing Cookies',
      marketingDesc: 'These cookies are used to track visitors across websites. The intention is to display ads that are relevant and engaging for the individual user.',
      marketingDetails: 'Examples: Advertising platform cookies, retargeting pixels, conversion tracking.',
      always: 'Always Active',
      enabled: 'Enabled',
      disabled: 'Disabled',
      saveButton: 'Save Preferences',
      clearButton: 'Clear All Cookie Data',
      clearConfirm: 'Are you sure you want to clear all cookie preferences? This will reset your settings and you will see the cookie banner again.',
      savedMessage: 'Your preferences have been saved successfully!',
      backHome: 'Back to Home',
      learnMore: 'Learn More',
      privacyPolicy: 'Privacy Policy',
    },
    de: {
      title: 'Cookie-Einstellungen',
      description: 'Verwalten Sie Ihre Cookie-Einstellungen für diese Website. Funktionale Cookies sind immer aktiviert, da sie für die ordnungsgemäße Funktion der Website erforderlich sind.',
      functional: 'Funktionale Cookies',
      functionalDesc: 'Diese Cookies sind für die ordnungsgemäße Funktion der Website unerlässlich. Sie ermöglichen grundlegende Funktionen wie Authentifizierung, Sicherheit und Sitzungsverwaltung. Diese Cookies können nicht deaktiviert werden.',
      functionalDetails: 'Beispiele: Authentifizierungstoken (Clerk), Sitzungsverwaltung, Sicherheitstoken, Spracheinstellungen.',
      analytics: 'Analyse-Cookies',
      analyticsDesc: 'Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren, indem sie Informationen anonym sammeln und melden. Dies hilft uns, unsere Website und Dienstleistungen zu verbessern.',
      analyticsDetails: 'Beispiele: Google Analytics, Seitenaufrufe, Nutzerverhalten.',
      marketing: 'Marketing-Cookies',
      marketingDesc: 'Diese Cookies werden verwendet, um Besucher über Websites hinweg zu verfolgen. Die Absicht ist, Anzeigen zu zeigen, die für den einzelnen Nutzer relevant und ansprechend sind.',
      marketingDetails: 'Beispiele: Werbe-Plattform-Cookies, Retargeting-Pixel, Conversion-Tracking.',
      always: 'Immer aktiv',
      enabled: 'Aktiviert',
      disabled: 'Deaktiviert',
      saveButton: 'Einstellungen speichern',
      clearButton: 'Alle Cookie-Daten löschen',
      clearConfirm: 'Sind Sie sicher, dass Sie alle Cookie-Einstellungen löschen möchten? Dies setzt Ihre Einstellungen zurück und Sie sehen das Cookie-Banner erneut.',
      savedMessage: 'Ihre Einstellungen wurden erfolgreich gespeichert!',
      backHome: 'Zurück zur Startseite',
      learnMore: 'Mehr erfahren',
      privacyPolicy: 'Datenschutz',
    },
    uk: {
      title: 'Налаштування файлів cookie',
      description: 'Керуйте налаштуваннями файлів cookie для цього сайту. Функціональні файли cookie завжди ввімкнені, оскільки вони необхідні для правильної роботи сайту.',
      functional: 'Функціональні файли cookie',
      functionalDesc: 'Ці файли cookie необхідні для правильної роботи сайту. Вони забезпечують базові функції, такі як аутентифікація, безпека та управління сеансами. Ці файли cookie не можна вимкнути.',
      functionalDetails: 'Приклади: токени аутентифікації (Clerk), управління сеансами, токени безпеки, мовні налаштування.',
      analytics: 'Аналітичні файли cookie',
      analyticsDesc: 'Ці файли cookie допомагають нам зрозуміти, як відвідувачі взаємодіють з нашим сайтом, збираючи та повідомляючи інформацію анонімно. Це допомагає нам покращувати наш сайт та послуги.',
      analyticsDetails: 'Приклади: Google Analytics, відстеження переглядів сторінок, аналіз поведінки користувачів.',
      marketing: 'Маркетингові файли cookie',
      marketingDesc: 'Ці файли cookie використовуються для відстеження відвідувачів на різних сайтах. Мета полягає в показі реклами, яка є релевантною та цікавою для окремого користувача.',
      marketingDetails: 'Приклади: файли cookie рекламних платформ, пікселі ретаргетингу, відстеження конверсій.',
      always: 'Завжди активні',
      enabled: 'Увімкнено',
      disabled: 'Вимкнено',
      saveButton: 'Зберегти налаштування',
      clearButton: 'Очистити всі дані cookie',
      clearConfirm: 'Ви впевнені, що хочете очистити всі налаштування файлів cookie? Це скине ваші налаштування, і ви знову побачите банер cookie.',
      savedMessage: 'Ваші налаштування успішно збережено!',
      backHome: 'Повернутися на головну',
      learnMore: 'Дізнатися більше',
      privacyPolicy: 'Політика конфіденційності',
    },
  };

  const t = translations[locale as keyof typeof translations] || translations.en;

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
          <Title size="md" className="font-bold mb-4">{t.title}</Title>
          <p className="text-gray-600 mb-4">{t.description}</p>
          <Link
            href={`/${locale}/privacy`}
            className="text-blue-600 hover:text-blue-800 underline inline-flex items-center"
          >
            {t.privacyPolicy} →
          </Link>
        </div>

        {/* Success Message */}
        {saved && (
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg mb-6">
            ✓ {t.savedMessage}
          </div>
        )}

        {/* Cookie Categories */}
        <div className="space-y-4">
          {/* Functional Cookies */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <Title size="sm" className="font-semibold mb-2">{t.functional}</Title>
                <p className="text-gray-600 mb-2">{t.functionalDesc}</p>
                <p className="text-sm text-gray-500 italic">{t.functionalDetails}</p>
              </div>
              <div className="ml-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  {t.always}
                </span>
              </div>
            </div>
          </div>

          {/* Analytics Cookies */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <Title size="sm" className="font-semibold mb-2">{t.analytics}</Title>
                <p className="text-gray-600 mb-2">{t.analyticsDesc}</p>
                <p className="text-sm text-gray-500 italic">{t.analyticsDetails}</p>
              </div>
              <div className="ml-4">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    {preferences.analytics ? t.enabled : t.disabled}
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Marketing Cookies */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <Title size="sm" className="font-semibold mb-2">{t.marketing}</Title>
                <p className="text-gray-600 mb-2">{t.marketingDesc}</p>
                <p className="text-sm text-gray-500 italic">{t.marketingDetails}</p>
              </div>
              <div className="ml-4">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    {preferences.marketing ? t.enabled : t.disabled}
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          <button
            onClick={savePreferences}
            className="flex-1 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t.saveButton}
          </button>
          <button
            onClick={clearAllCookies}
            className="flex-1 px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
          >
            {t.clearButton}
          </button>
        </div>

        {/* Back Link */}
        <div className="mt-6 text-center">
          <Link
            href={`/${locale}`}
            className="text-blue-600 hover:text-blue-800 underline"
          >
            ← {t.backHome}
          </Link>
        </div>
      </div>
    </div>
  );
}
